/**
 * Link CAD Tool - BIM 2025 Grade
 *
 * Imports/Links DWG and DXF files and renders their real geometry
 * (line work + text) in the scene.
 * Supports:
 * - Layer visibility control (Freeze/Thaw)
 * - Unit scaling (Auto-detected from $INSUNITS: Inches, Millimeters, Meters...)
 * - Positioning (Origin, Center, Shared)
 * - Color customization (Invert, Black & White)
 * - Conversion to model lines (Explode)
 * - Reload from source file via the element context menu
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { LinkManager, LinkInstance } from '../core/LinkManager';
import { ThreeEvent } from '@react-three/fiber';
import { parseCadFile, isParsableCad, CadParseResult } from '../services/cad/CadParserService';
import { CadLinkStore } from '../services/cad/CadLinkStore';
import { useAppStore } from '../state/store';
import i18n from '../i18n';

export interface CADLayer {
    name: string;
    visible: boolean;
    color: string;
    lineWeigth: number;
}

export interface LinkCADOptions {
    filePath: string;
    positioning: 'OriginToOrigin' | 'CenterToCenter';
    importUnits: 'Auto' | 'Millimeter' | 'Meter' | 'Inch' | 'Foot';
    placeAt: 'Level' | 'CurrentView';
    levelId: string;
    invertColors: boolean;
    layers: 'All' | 'Visible';
}

interface LinkCADElement extends Element {
    geometryData: {
        linkId: string;
        origin: [number, number, number];
        layers: CADLayer[];
    }
}

/**
 * Build the renderable geometryData payload from a parse result. Shared by
 * the Link and Import flows so both produce identical scene content. The
 * geometry is re-centered around its own bbox so the whole drawing sits
 * around the object origin regardless of how far the source file sits from
 * the CAD world origin (a very common situation in 2D exports).
 */
/**
 * Register a parsed DWG/DXF's layers into the CAD app layer store so the
 * app's layer language (visibility/lock/freeze/color) governs the drawing
 * just like 2D's import does. Existing layers keep their current state.
 * (docs/plans/2d plan V2/F2: import layer mapping.)
 */
export function registerImportedLayers(layers: CadParseResult['layers']): void {
    const app = useAppStore.getState();
    const byName = new Map(Object.values(app.layers).map((l) => [l.name, l]));
    let order = Object.keys(app.layers).length;
    layers.forEach((l) => {
        const existing = byName.get(l.name);
        if (existing) {
            app.updateLayer(existing.id, {
                visible: l.visible, locked: l.locked,
                frozen: l.frozen || existing.frozen,
                color: existing.color === '#ffffff' ? l.color : existing.color,
            });
            return;
        }
        app.addLayer({
            id: `layer-${l.name.replace(/\W+/g, '-')}-${Date.now().toString(36)}`,
            name: l.name, color: l.color, visible: l.visible, locked: l.locked,
            order: order++, frozen: l.frozen,
        });
    });
}

export function buildCadGeometryData(
    result: CadParseResult,
    origin: [number, number, number],
    linkId?: string
) {
    const [minX, minY, minZ, maxX, maxY, maxZ] = result.bounds;
    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2, cz = (minZ + maxZ) / 2;
    const rel = (p: [number, number, number]): [number, number, number] =>
        [p[0] - cx, p[1] - cy, p[2] - cz];
    // Side effect (intentional): every CAD import/link registers the file's
    // layers into the app layer store so both workbenches speak one language.
    registerImportedLayers(result.layers);
    return {
        linkId,
        origin,
        // Real parsed payload consumed by the CAD link renderer
        cad: {
            fileName: result.fileName,
            format: result.format,
            unitScale: result.unitScale,
            insunits: result.insunits,
            lineCount: result.lines.length,
            textCount: result.texts.length,
            bounds: result.bounds,
        },
        lines: result.lines.map(ln => ({
            ...ln,
            points: [rel(ln.points[0]), rel(ln.points[1])] as [[number, number, number], [number, number, number]],
        })),
        texts: result.texts.map(t => ({
            ...t,
            x: t.x - cx, y: t.y - cy, z: t.z - cz,
        })),
        layers: result.layers.map(l => ({
            name: l.name,
            visible: l.visible && !l.frozen,
            color: l.color,
            lineWeigth: 1,
        })),
        // Document-scoped layer snapshot: the CAD workspace registers these
        // into the app layer store (docs/plans/2d plan V2/F2 mapping).
        docLayers: result.layers.map(l => ({
            name: l.name,
            visible: l.visible,
            frozen: l.frozen,
            locked: l.locked,
            color: l.color,
        })),
        boxSize: [
            Math.max(maxX - minX, 1),
            Math.max(maxY - minY, 1),
            Math.max(maxZ - minZ, 1),
        ] as [number, number, number],
    };
}

/** Open a fresh file picker for re-linking an element whose session handle is lost. */
export function pickCadFile(): Promise<File | null> {
    return new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.dwg,.dxf';
        input.onchange = () => resolve(input.files?.[0] ?? null);
        input.oncancel = () => resolve(null);
        input.click();
    });
}

/**
 * Read + parse a CAD File into a geometryData payload. Throws with a
 * user-facing message on unsupported/corrupt files.
 */
export async function readCadFile(file: File): Promise<CadParseResult> {
    if (!isParsableCad(file.name)) {
        throw new Error(`"${file.name}" is not a DWG/DXF file.`);
    }
    const buffer = await file.arrayBuffer();
    return parseCadFile(file.name, buffer);
}

export class LinkCADTool extends BaseTool {
    id = 'link_cad';
    name = 'Link CAD';
    cursor = 'default';

    private linkManager: LinkManager;

    constructor(linkManager: LinkManager) {
        super();
        this.linkManager = linkManager;
    }

    private options: LinkCADOptions = {
        filePath: '',
        positioning: 'OriginToOrigin',
        importUnits: 'Auto',
        placeAt: 'Level',
        levelId: 'Level 1',
        invertColors: false,
        layers: 'Visible'
    };

    /**
     * Set options (usually from a modal dialog)
     */
    setOptions(options: Partial<LinkCADOptions>) {
        this.options = { ...this.options, ...options };
    }

    onActivate(): void {
        // Dialog-driven command: BIM opens a file dialog for Link CAD.
        void this.pickFile('.dwg,.dxf').then(async (file) => {
            if (file) {
                await this.linkCadFile(file);
            }
            this.exitToSelect();
        });
    }

    /**
     * Execute the "Link CAD" command with a known file path (sync placeholder
     * element, kept for programmatic/test use).
     */
    public executeLink(filePath: string): LinkInstance {
        this.options.filePath = filePath;

        // 1. Create the link instance via Manager
        const link = this.linkManager.linkFile('CAD', filePath, {
            positioning: this.options.positioning
        });

        // 2. Create the wrapper element in the BIM model
        const element = this.createCADElement(link);
        // Bind the wrapper so Manage Links can act on the scene object.
        this.linkManager.setDocumentId(link.id, element.id);

        return link;
    }

    /**
     * Full link flow with a real File: parse geometry first, then create the
     * element so the scene receives drawing line work immediately.
     */
    public async linkCadFile(file: File): Promise<LinkInstance | null> {
        this.options.filePath = file.name;

        const link = this.linkManager.linkFile('CAD', file.name, {
            positioning: this.options.positioning,
            size: file.size
        });

        const element = this.createCADElement(link);
        // Bind the wrapper element so Manage Links can reload/hide/remove it.
        this.linkManager.setDocumentId(link.id, element.id);

        // Parse asynchronously; the element shows a placeholder until geometry
        // arrives, then it is patched in-place via updateElement.
        try {
            const result = await readCadFile(file);
            this.applyParsedToElement(link.id, file, result);
            this.notify(i18n.t('tools.notify.cadLinked', { fileName: file.name, count: result.lines.length }), 'success');
        } catch (err) {
            this.notify(i18n.t('tools.notify.cadReadFailed', { message: (err as Error).message }), 'error');
        }

        return link;
    }

    /** Patch the freshly-parsed geometry into the wrapper element. */
    private applyParsedToElement(linkId: string, file: File, result: CadParseResult) {
        if (!this.context) return;
        // Find the wrapper element for this link
        const elements = Array.from(this.context.bimStore.project.elements.values());
        const el = elements.find(e => (e as LinkCADElement).geometryData?.linkId === linkId);
        if (!el) return;
        applyParsedGeometryToElement(el.id, file, result, this.context.bimStore);
    }

    onPointerDown(_event: ThreeEvent<PointerEvent>): void {
        // CAD linking is usually a dialog-based command, not interactive placement
        // However, if manual placement is selected, we'd handle it here
    }

    onPointerMove(_event: ThreeEvent<PointerEvent>): void {
        // Not used
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void {
        // Not used
    }

    private createCADElement(link: LinkInstance): LinkCADElement {
        const cadElement: LinkCADElement = {
            id: uuidv4(),
            category: 'CAD Links',
            type: 'Linked CAD: ' + link.name,
            parameters: {
                'File Path': link.path,
                'Scale': '1.0',
                'Import Units': this.options.importUnits
            },
            constraints: [
                {
                    type: 'LEVEL_BASED',
                    targetId: this.options.levelId,
                    data: { offset: 0 }
                }
            ],
            geometryData: {
                // Renderer: placeholder until parse completes, then patched
                linkId: link.id,
                origin: [0, 0, 0],
                layers: this.getMockLayers()
            }
        };

        this.context?.bimStore.addElement(cadElement);
        return cadElement;
    }

    private getMockLayers(): CADLayer[] {
        return [
            { name: '0', visible: true, color: '#FFFFFF', lineWeigth: 1 },
            { name: 'A-WALL', visible: true, color: '#FFFF00', lineWeigth: 2 },
            { name: 'A-DOOR', visible: true, color: '#00FF00', lineWeigth: 1 }
        ];
    }
}

/**
 * Apply a parsed result + remembered file to an existing CAD element (Link
 * or Import). Re-centers the drawing around the element origin, exactly like
 * the fresh link flow. Exposed for the Reload command in the context menu;
 * takes the BIM store state snapshot so callers can pass
 * `useBIMStore.getState()` without this module importing the store (keeps
 * tool deps one-directional).
 */
export function applyParsedGeometryToElement(
    elementId: string,
    file: File | null,
    result: CadParseResult,
    bim: { project: { elements: Map<string, any> }; updateElement: (id: string, updates: Partial<any>) => void }
): void {
    const el = bim.project.elements.get(elementId) as any;
    if (!el) return;
    const geom = el.geometryData ?? {};
    const origin = (geom.origin ?? [0, 0, 0]) as [number, number, number];
    const payload = buildCadGeometryData(result, origin, geom.linkId);
    bim.updateElement(elementId, {
        geometryData: { ...geom, ...payload },
        parameters: {
            ...(el.parameters ?? {}),
            'File Path': result.fileName,
            'Import Units': String(result.insunits),
            'Line Count': String(result.lines.length),
        },
    });
    if (file) {
        CadLinkStore.setSource(elementId, file, file.name);
    }
}
